import React, { useEffect, useState } from 'react';
import { Modal, Button, Typography, Space, Tag } from 'antd';
import {
  TrophyOutlined,
  GiftOutlined,
  UnlockOutlined,
  ArrowRightOutlined,
} from '@ant-design/icons';
import { RankIcon } from './RankComponents';
import confetti from 'canvas-confetti';

const { Title, Paragraph, Text } = Typography;

/**
 * ランクアップモーダル
 * ランクアップ時に表示される祝福モーダル
 */
const RankUpModal = ({ visible, onClose, rankUpData }) => {
  const [showContent, setShowContent] = useState(false);

  useEffect(() => {
    if (visible) {
      // モーダル表示時に紙吹雪アニメーション
      const duration = 3000;
      const animationEnd = Date.now() + duration;
      const defaults = {
        startVelocity: 25,
        spread: 360,
        ticks: 60,
        zIndex: 2000,
        scalar: 1.2,
      };

      const randomInRange = (min, max) => Math.random() * (max - min) + min;

      const interval = setInterval(() => {
        const timeLeft = animationEnd - Date.now();

        if (timeLeft <= 0) {
          clearInterval(interval);
          return;
        }

        const particleCount = 30 * (timeLeft / duration);

        // 左側から
        confetti({
          ...defaults,
          particleCount,
          origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
        });
        // 右側から
        confetti({
          ...defaults,
          particleCount,
          origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
        });
      }, 300);

      // コンテンツを少し遅延表示
      setTimeout(() => setShowContent(true), 300);

      return () => {
        clearInterval(interval);
        setShowContent(false);
      };
    }
  }, [visible]);

  if (!rankUpData) return null;

  // バックエンドからのrank_update構造に対応
  const newRank = rankUpData.new_rank || rankUpData.rank_after;
  const newRankDisplay = rankUpData.new_rank_display;
  const oldRank = rankUpData.old_rank || rankUpData.rank_before;
  const rewards = rankUpData.rewards || [];

  // 報酬を整理
  const bonusCredits =
    rewards.find(r => r.type === 'bonus_credit')?.amount || 0;
  const featureUnlocks = rewards.filter(r => r.type === 'feature_unlock');
  const discounts = rewards.filter(r => r.type === 'discount' || r.type === 'permanent_discount');

  // ランクゲート機能名のマッピング（プランベース移行後、ランクで解放されるのは紹介機能・ファクトチェックのみ）
  const getFeatureName = featureKey => {
    const featureNames = {
      'referral_system': '紹介機能',
      'fact_check': 'ファクトチェックモード',
      'fact_check_mode': 'ファクトチェックモード',
    };
    return featureNames[featureKey] || featureKey;
  };

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      width={720}
      centered
      closable={false}
      maskClosable={false}
      styles={{
        body: {
          background: '#ffffff',
          padding: '60px 48px',
          borderRadius: 8,
        },
      }}
    >
      <div
        style={{
          opacity: showContent ? 1 : 0,
          transform: showContent ? 'scale(1)' : 'scale(0.9)',
          transition: 'all 0.5s ease-out',
        }}
      >
        {/* ランクアップアニメーション */}
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <Title
            level={2}
            style={{
              color: '#1890ff',
              marginBottom: 32,
              fontSize: 28,
              fontWeight: 600,
            }}
          >
            ランクアップしました！
          </Title>

          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'flex-end',
              gap: 32,
            }}
          >
            {oldRank && (
              <>
                <div>
                  <div
                    style={{ color: '#8c8c8c', fontSize: 12, marginBottom: 6 }}
                  >
                    これまで
                  </div>
                  <RankIcon rank={oldRank} size={40} showName />
                </div>
                <div style={{ marginBottom: 12 }}>
                  <ArrowRightOutlined
                    style={{ fontSize: 28, color: '#bfbfbf' }}
                  />
                </div>
              </>
            )}
            <div>
              <div style={{ color: '#8c8c8c', fontSize: 13, marginBottom: 8 }}>
                新しいランク
              </div>
              <RankIcon rank={newRank} size={48} showName />
            </div>
          </div>
        </div>

        {/* 特典リスト */}
        {(bonusCredits > 0 ||
          featureUnlocks.length > 0 ||
          discounts.length > 0) && (
          <div
            style={{
              background: '#fafafa',
              borderRadius: 12,
              padding: 32,
              marginBottom: 32,
              border: '1px solid #e8e8e8',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginBottom: 20,
              }}
            >
              <GiftOutlined style={{ fontSize: 20, color: '#1890ff' }} />
              <Title
                level={4}
                style={{ margin: 0, color: '#262626', fontSize: 18 }}
              >
                獲得した特典
              </Title>
            </div>

            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              {/* ボーナスクレジット */}
              {bonusCredits > 0 && (
                <div
                  style={{
                    padding: '16px 20px',
                    background: '#ffffff',
                    borderRadius: 8,
                    border: '2px solid #d3adf7',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Text strong style={{ fontSize: 16 }}>
                      ボーナスクレジット
                    </Text>
                    <Text strong style={{ fontSize: 20, color: '#722ed1' }}>
                      +{bonusCredits}
                    </Text>
                  </div>
                </div>
              )}

              {/* 機能アンロック */}
              {featureUnlocks.map((feature, index) => {
                const featureName = getFeatureName(
                  feature.feature_key ||
                    feature.feature_name ||
                    feature.feature,
                );

                return (
                  <div
                    key={index}
                    style={{
                      padding: '16px 20px',
                      background: '#ffffff',
                      borderRadius: 8,
                      border: '2px solid #b7eb8f',
                    }}
                  >
                    <div
                      style={{ display: 'flex', alignItems: 'center', gap: 12 }}
                    >
                      <UnlockOutlined
                        style={{ fontSize: 20, color: '#52c41a' }}
                      />
                      <div>
                        <Text strong style={{ fontSize: 16, display: 'block' }}>
                          {featureName}
                        </Text>
                        {feature.feature_description && (
                          <Text type="secondary" style={{ fontSize: 13 }}>
                            {feature.feature_description}
                          </Text>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* 割引 */}
              {discounts.map((discount, index) => {
                // バックエンドは rate (0.10 = 10%) または discount_percent を使用
                const discountPercent = discount.discount_percent || (discount.rate ? Math.round(discount.rate * 100) : 0);
                
                return (
                  <div
                    key={index}
                    style={{
                      padding: '16px 20px',
                      background: '#ffffff',
                      borderRadius: 8,
                      border: '2px solid #ffadd2',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Text strong style={{ fontSize: 16 }}>
                        {discount.scope === 'all'
                          ? '全プラン割引'
                          : '永続プラン割引'}
                      </Text>
                      <Tag
                        color="magenta"
                        style={{ fontSize: 16, padding: '4px 12px' }}
                      >
                        {discountPercent}% OFF
                      </Tag>
                    </div>
                  </div>
                );
              })}
            </Space>
          </div>
        )}

        {/* 閉じるボタン */}
        <div style={{ textAlign: 'center' }}>
          <Button
            type="primary"
            size="large"
            onClick={onClose}
            style={{
              height: 48,
              fontSize: 16,
              fontWeight: 600,
              minWidth: 200,
            }}
          >
            続ける
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default RankUpModal;
